import type { GetBalancesResponse } from "@defuse-protocol/one-click-sdk-typescript"
import { findSingleTokenFundingMatch } from "@src/components/DefuseSDK/utils/paymentFunding"
import type { QueryClient } from "@tanstack/react-query"
import { createTokenUsdPricesQueryOptions } from "../../hooks/useTokensUsdPrices"
import { createDepositedBalanceQueryOptions } from "../../queries/balanceQueries"
import type { TokenInfo } from "../../types/base"
import type { IntentsUserId } from "../../types/intentsUserId"
import { getTokenId } from "../../utils/token"
import { getUnderlyingBaseTokenInfos } from "../../utils/tokenUtils"
import { createPrivateBalanceQueryOptions } from "../account/queries/privateBalanceQueries"
import { buildPrivateBalanceMap } from "../account/utils/privateHoldingsMapper"

const SLIPPAGE_BPS = 5000
const FEE_BUFFER_PCT = 1.5

export type ModeStatus =
  | { kind: "qualifies"; token?: TokenInfo }
  | { kind: "doesnt_qualify" }
  | { kind: "unknown" }

export type FundingCoverage = { main: ModeStatus; confidential: ModeStatus }

function hasAnySingleAssetBalance(balances: Record<string, bigint>): boolean {
  return Object.values(balances).some((balance) => balance > 0n)
}

export async function preCheckFunding({
  destinationToken,
  requestedAmount,
  tokenList,
  userId,
  fetchQuery,
}: {
  destinationToken: TokenInfo
  requestedAmount: { amount: bigint; decimals: number }
  tokenList: TokenInfo[]
  userId: IntentsUserId
  fetchQuery: QueryClient["fetchQuery"]
}): Promise<FundingCoverage> {
  const tokenIds = tokenList
    .flatMap((t) => getUnderlyingBaseTokenInfos(t))
    .map(getTokenId)

  const [mainResult, confResult, pricesResult] = await Promise.allSettled([
    fetchQuery(
      createDepositedBalanceQueryOptions({ userId, tokenIds })
    ) as Promise<Record<string, bigint>>,
    fetchQuery(
      createPrivateBalanceQueryOptions({ userId })
    ) as Promise<GetBalancesResponse>,
    fetchQuery(createTokenUsdPricesQueryOptions()) as Promise<
      Record<string, { price: number }>
    >,
  ])

  if (pricesResult.status === "rejected") {
    return {
      main: { kind: "unknown" },
      confidential: { kind: "unknown" },
    }
  }

  let tokenPrices: Record<string, number>
  try {
    tokenPrices = Object.fromEntries(
      Object.entries(pricesResult.value).map(([id, info]) => [id, info.price])
    )
  } catch {
    return {
      main: { kind: "unknown" },
      confidential: { kind: "unknown" },
    }
  }

  const usdBufferMultiplier =
    1 + SLIPPAGE_BPS / 1_000_000 + FEE_BUFFER_PCT / 100

  function classify(
    queryResult: PromiseSettledResult<unknown>,
    transform: (v: unknown) => Record<string, bigint>
  ): ModeStatus {
    if (queryResult.status === "rejected") return { kind: "unknown" }
    try {
      const balances = transform(queryResult.value)
      const match = findSingleTokenFundingMatch({
        destinationToken,
        requestedAmount,
        candidateTokens: tokenList,
        balances,
        tokenPrices,
        usdBufferMultiplier,
      })

      if (match?.kind === "same_asset") {
        return { kind: "qualifies", token: undefined }
      }
      if (match?.kind === "cross_asset") {
        return { kind: "qualifies", token: match.token }
      }
      if (match?.kind === "unknown") {
        return { kind: "unknown" }
      }

      return { kind: "doesnt_qualify" }
    } catch {
      return { kind: "unknown" }
    }
  }

  return {
    main: classify(mainResult, (v) => v as Record<string, bigint>),
    confidential: classify(confResult, (v) =>
      buildPrivateBalanceMap(v as GetBalancesResponse)
    ),
  }
}

export async function preCheckFundingPresence({
  tokenList,
  userId,
  fetchQuery,
}: {
  tokenList: TokenInfo[]
  userId: IntentsUserId
  fetchQuery: QueryClient["fetchQuery"]
}): Promise<FundingCoverage> {
  const tokenIds = tokenList
    .flatMap((t) => getUnderlyingBaseTokenInfos(t))
    .map(getTokenId)

  const [mainResult, confResult] = await Promise.allSettled([
    fetchQuery(
      createDepositedBalanceQueryOptions({ userId, tokenIds })
    ) as Promise<Record<string, bigint>>,
    fetchQuery(
      createPrivateBalanceQueryOptions({ userId })
    ) as Promise<GetBalancesResponse>,
  ])

  function classify(
    queryResult: PromiseSettledResult<unknown>,
    transform: (v: unknown) => Record<string, bigint>
  ): ModeStatus {
    if (queryResult.status === "rejected") return { kind: "unknown" }

    try {
      return hasAnySingleAssetBalance(transform(queryResult.value))
        ? { kind: "qualifies" }
        : { kind: "doesnt_qualify" }
    } catch {
      return { kind: "unknown" }
    }
  }

  return {
    main: classify(mainResult, (v) => v as Record<string, bigint>),
    confidential: classify(confResult, (v) =>
      buildPrivateBalanceMap(v as GetBalancesResponse)
    ),
  }
}
